import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiBarChart2, FiLoader, FiRefreshCw, FiPauseCircle, FiPlayCircle, FiX, FiCheck, FiUsers, FiInfo, FiCalendar, FiMail, FiActivity, FiBox, FiKey, FiChevronRight, FiAlertTriangle, FiEdit2, FiSave, FiAlertCircle, FiSend, FiClock } = FiIcons;

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, loading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center animate-in zoom-in-95 duration-200">
        <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiAlertTriangle} className="text-3xl" />
        </div>
        <h3 className="text-lg font-bold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500 mt-2">{message}</p>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="flex-1 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
            {loading && <SafeIcon icon={FiLoader} className="animate-spin" />} Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientDrawer = ({ client, apps, keys, queue, onClose, onRefresh }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [editForm, setEditForm] = useState({
    client_name: client.client_name || client.name || '',
    contact_email: client.contact_email || '',
    monthly_limit: client.monthly_limit || 0,
    annual_limit: client.annual_limit || 0,
    notes: client.notes || '',
    from_name: client.from_name || '',
    from_email: client.from_email || '',
    reply_to_email: client.reply_to_email || ''
  });

  if (!client) return null;

  const clientApps = apps.filter(a => a.client_id === client.id);
  const clientKeys = keys.filter(k => k.client_id === client.id);
  const clientQueue = queue.filter(q => q.client_id === client.id);
  
  const queuedCount = clientQueue.filter(q => q.status === 'queued' || q.status === 'draft').length;
  const sentCount = clientQueue.filter(q => q.status === 'sent').length;
  const failedCount = clientQueue.filter(q => q.status === 'failed').length;
  
  const latestQueue = [...clientQueue]
    .sort((a, b) => new Date(b.created) - new Date(a.created))
    .slice(0, 5);

  const isActive = client.status === 'active' || client.status === 'enabled';

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_client_status', payload: { client_id: client.id, status: newStatus } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      showToast(`Client ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      onRefresh();
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsUpdating(false);
      setShowConfirm(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.client_name) return showToast('Client name is required', 'error');
    setIsUpdating(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_client', payload: { id: client.id, ...editForm } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      showToast('Client updated successfully');
      setIsEditing(false);
      onRefresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={FiUsers} /> Client 360</h3>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5">
                <SafeIcon icon={FiEdit2} /> Edit
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
          {/* Header Info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-3xl font-black shrink-0">
              {(client.client_name || client.name || 'C').charAt(0)}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-slate-900 truncate">{client.client_name || client.name}</h2>
              <p className="text-sm text-slate-500 truncate">{client.contact_email || 'No contact email'}</p>
              <div className="mt-1 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {client.status}
                </span>
                <span className="text-[10px] text-slate-400 font-mono">ID: {client.id}</span>
              </div>
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-top-4">
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={FiUsers} /> Profile Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Client Name *</label>
                    <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.client_name} onChange={e => setEditForm({...editForm, client_name: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Contact Email</label>
                    <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.contact_email} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Monthly Limit</label>
                    <input type="number" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.monthly_limit} onChange={e => setEditForm({...editForm, monthly_limit: Number(e.target.value)})} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Annual Limit</label>
                    <input type="number" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.annual_limit} onChange={e => setEditForm({...editForm, annual_limit: Number(e.target.value)})} />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Internal Notes</label>
                    <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs resize-none h-16" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} />
                  </div>
                </div>
              </div>

              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={FiMail} /> Default Sender Identity</h4>
                <p className="text-[10px] text-slate-500 leading-tight">These values act as fallbacks if API keys or apps do not specify sender details.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">From Name</label>
                    <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.from_name} onChange={e => setEditForm({...editForm, from_name: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">From Email</label>
                    <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.from_email} onChange={e => setEditForm({...editForm, from_email: e.target.value})} />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Reply-To Email</label>
                    <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editForm.reply_to_email} onChange={e => setEditForm({...editForm, reply_to_email: e.target.value})} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setIsEditing(false)} className="px-5 py-2.5 bg-slate-100 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-200 transition-colors">Cancel</button>
                <button onClick={handleSaveEdit} disabled={isUpdating} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
                  {isUpdating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />} Save Changes
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Profile & Limits View */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Monthly Limit</p>
                  <p className="text-sm font-bold text-slate-700">{(client.monthly_limit || 0).toLocaleString()} / mo</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Annual Limit</p>
                  <p className="text-sm font-bold text-slate-700">{(client.annual_limit || 0).toLocaleString()} / yr</p>
                </div>
                {client.notes && (
                  <div className="col-span-2 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Internal Notes</p>
                    <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{client.notes}</p>
                  </div>
                )}
              </div>

              {/* Sender Identity View */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiMail} /> Default Sender Identity
                </h4>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-600 space-y-2">
                  <div className="flex justify-between">
                    <span className="font-bold text-slate-400">From Name:</span>
                    <span className="font-medium text-slate-800">{client.from_name || <span className="italic opacity-50">Not configured</span>}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-bold text-slate-400">From Email:</span>
                    <span className="font-medium text-slate-800">{client.from_email || <span className="italic opacity-50">Not configured</span>}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-bold text-slate-400">Reply-To:</span>
                    <span className="font-medium text-slate-800">{client.reply_to_email || <span className="italic opacity-50">Not configured</span>}</span>
                  </div>
                </div>
              </div>

              {/* Email Activity View */}
              <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiActivity} /> Email Activity
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-center">
                    <p className="text-[10px] font-black text-blue-400 uppercase">Queued</p>
                    <p className="text-lg font-bold text-blue-700">{queuedCount}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-center">
                    <p className="text-[10px] font-black text-emerald-400 uppercase">Sent</p>
                    <p className="text-lg font-bold text-emerald-700">{sentCount}</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-center">
                    <p className="text-[10px] font-black text-red-400 uppercase">Failed</p>
                    <p className="text-lg font-bold text-red-700">{failedCount}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h5 className="text-[10px] font-bold text-slate-500 uppercase mt-4 mb-2">Recent Routing Log</h5>
                  {latestQueue.length === 0 ? (
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl text-center text-xs text-slate-400 italic">
                      No email activity yet.
                    </div>
                  ) : (
                    latestQueue.map(q => (
                      <div key={q.id} className="p-3 bg-white border border-slate-200 rounded-xl flex flex-col gap-1.5 shadow-sm">
                        <div className="flex justify-between items-start gap-4">
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800 truncate">{q.to_email}</p>
                            <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{q.subject}</p>
                          </div>
                          <span className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-bold uppercase ${q.status === 'sent' ? 'bg-emerald-50 text-emerald-600' : q.status === 'failed' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                            {q.status}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-[9px] text-slate-400">
                          <span className="flex items-center gap-1"><SafeIcon icon={FiClock} /> {new Date(q.sent_at || q.created).toLocaleString()}</span>
                        </div>
                        {q.error_message && (
                          <div className="mt-1 p-1.5 bg-red-50/50 rounded text-[9px] text-red-500 font-mono break-all line-clamp-2">
                            {q.error_message}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Linked Applications */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <SafeIcon icon={FiBox} /> Linked Applications ({clientApps.length})
                </h4>
                <div className="space-y-2">
                  {clientApps.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No applications linked.</p>
                  ) : (
                    clientApps.map(app => (
                      <div key={app.id} className="p-3 bg-white border border-slate-100 rounded-xl flex justify-between items-center">
                        <div className="min-w-0">
                          <span className="text-xs font-bold text-slate-700 truncate block">{app.app_name || app.name}</span>
                          {app.allowed_origins && <span className="text-[9px] text-slate-400 font-mono truncate block mt-0.5">{app.allowed_origins}</span>}
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 bg-slate-50 rounded uppercase font-bold text-slate-500 shrink-0">{app.status}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* API Keys */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <SafeIcon icon={FiKey} /> Active API Keys ({clientKeys.length})
                  </h4>
                  <button 
                    onClick={() => showToast('Open API Keys page to generate a key for this client.', 'info')}
                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded transition-colors"
                  >
                    + Generate API Key
                  </button>
                </div>
                <div className="space-y-3">
                  {clientKeys.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No API keys issued.</p>
                  ) : (
                    clientKeys.map(key => (
                      <div key={key.id} className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-sm">
                        <div className="flex justify-between items-start">
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800 truncate">{key.key_name || key.name}</p>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">{key.key_prefix}••••••••</p>
                          </div>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold shrink-0 ${key.status === 'active' ? 'text-emerald-600 bg-emerald-50' : 'text-red-500 bg-red-50'}`}>{key.status}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[10px] bg-slate-50 p-2 rounded-lg border border-slate-100">
                          <div><span className="text-slate-400 font-bold">Monthly:</span> <span className="font-mono text-slate-700">{key.monthly_limit || 0}</span></div>
                          <div><span className="text-slate-400 font-bold">Annual:</span> <span className="font-mono text-slate-700">{key.annual_limit || 0}</span></div>
                          <div className="col-span-2 truncate"><span className="text-slate-400 font-bold">Origins:</span> <span className="font-mono text-slate-700">{key.allowed_origins || 'All (*)'}</span></div>
                        </div>
                        <div className="flex justify-between items-center text-[9px] text-slate-400">
                          <span>Created: {new Date(key.created).toLocaleDateString()}</span>
                          <span>Last used: {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="pt-4 flex items-center justify-between text-[10px] text-slate-400 font-medium">
                <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} /> Created {new Date(client.created).toLocaleDateString()}</span>
                <span>Updated {new Date(client.updated).toLocaleDateString()}</span>
              </div>
            </>
          )}
        </div>
        
        <div className="p-6 border-t border-slate-100 bg-slate-50 grid grid-cols-2 gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-100 transition-all">Close Drawer</button>
          <button 
            disabled={isUpdating || isEditing} 
            onClick={() => isActive ? setShowConfirm(true) : handleStatusUpdate('active')}
            className={`px-4 py-2.5 text-white text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 ${isEditing ? 'opacity-50 cursor-not-allowed bg-slate-400' : isActive ? 'bg-red-600 hover:bg-red-700 shadow-red-200' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'}`}
          >
            {isUpdating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isActive ? FiPauseCircle : FiPlayCircle} />}
            {isActive ? 'Suspend Client' : 'Reactivate Client'}
          </button>
        </div>
      </div>
      
      <ConfirmationModal 
        isOpen={showConfirm} 
        title="Suspend Client?" 
        message={`This will block all email routing for ${client.client_name || client.name}. Are you sure?`} 
        onConfirm={() => handleStatusUpdate('suspended')} 
        onCancel={() => setShowConfirm(false)} 
        loading={isUpdating} 
      />
    </div>
  );
};

const CreateClientModal = ({ onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    industry: '',
    contact_email: '',
    status: 'active',
    monthly_limit: 25000,
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name) return showToast('Client Name is required', 'error');
    
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_client', payload: formData })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      onSave(result.data);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiUsers} /> Add New Client</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Name *</label>
            <input required type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:bg-white focus:border-blue-500" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Industry</label>
              <input type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly Limit</label>
              <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono" value={formData.monthly_limit} onChange={e => setFormData({...formData, monthly_limit: parseInt(e.target.value)})} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Email</label>
            <input type="email" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notes</label>
            <textarea className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs h-20 resize-none" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 flex items-center gap-2">
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />} Save Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [keys, setKeys] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resClients, resApps, resKeys, resQueue] = await Promise.all([
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_clients' }) }).then(r => r.json()),
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_apps' }) }).then(r => r.json()),
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_api_keys' }) }).then(r => r.json()),
        fetch('/.netlify/functions/gateway', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'list_email_queue' }) }).then(r => r.json())
      ]);
      setClients(resClients.success ? resClients.data : []);
      setApps(resApps.success ? resApps.data : []);
      setKeys(resKeys.success ? resKeys.data : []);
      setQueue(resQueue.success ? resQueue.data : []);
    } catch (err) {
      showToast('Data retrieval failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (e, id, currentStatus) => {
    e.stopPropagation();
    setUpdatingId(id);
    const newStatus = (currentStatus === 'active' || currentStatus === 'enabled') ? 'suspended' : 'active';
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_client_status', payload: { client_id: id, status: newStatus } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      showToast(`Client ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = clients.filter(c => (c.client_name || c.name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Clients</h2>
          <p className="text-sm text-slate-500">Manage business entities and billing groups.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 shadow-sm transition-all" title="Refresh list">
            <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-md flex items-center gap-2">
            <SafeIcon icon={FiPlus} /> Add Client
          </button>
        </div>
      </div>

      <div className="flex bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input type="text" placeholder="Search clients..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-3xl text-blue-600 animate-spin" />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-left whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
                <th className="px-6 py-4">Client / Industry</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-50">
              {filtered.map(client => (
                <tr key={client.id} className="hover:bg-slate-50 transition-colors cursor-pointer group" onClick={() => setSelectedClient(client)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-bold shrink-0">
                        {(client.client_name || client.name || 'C').charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{client.client_name || client.name}</p>
                        <p className="text-xs text-slate-500">{client.industry || 'General'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${client.status === 'active' || client.status === 'enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end items-center gap-2">
                      <button 
                        onClick={(e) => handleUpdateStatus(e, client.id, client.status)} 
                        disabled={updatingId === client.id}
                        className={`p-2 rounded-lg transition-colors ${client.status === 'active' || client.status === 'enabled' ? 'text-red-400 hover:bg-red-50 hover:text-red-600' : 'text-emerald-400 hover:bg-emerald-50 hover:text-emerald-600'}`}
                        title={client.status === 'active' ? 'Suspend Client' : 'Activate Client'}
                      >
                        <SafeIcon icon={updatingId === client.id ? FiLoader : (client.status === 'active' || client.status === 'enabled' ? FiPauseCircle : FiPlayCircle)} className={updatingId === client.id ? 'animate-spin' : ''} />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedClient(client); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white rounded-lg text-xs font-bold transition-all shadow-sm group-hover:bg-blue-500 group-hover:text-white"
                      >
                        Details <SafeIcon icon={FiChevronRight} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && <CreateClientModal onClose={() => setShowAddModal(false)} onSave={() => { setShowAddModal(false); fetchData(); showToast('Client created successfully'); }} />}
      
      {selectedClient && (
        <ClientDrawer 
          client={selectedClient} 
          apps={apps} 
          keys={keys} 
          queue={queue}
          onClose={() => setSelectedClient(null)} 
          onRefresh={fetchData} 
        />
      )}
    </div>
  );
};

export default Clients;